import React, { useState, useEffect, useRef } from 'react';
import { useOrion } from '../../context/OrionContext';
import {
  buscarTicket, resolverSolicitantePorTicket,
  verificarDonoDoTicket, ticketsEmAtendimentoDoOperador, dispararCargaDoDia,
} from '../../services/mysuiteApi';
import { carregarListas, criticidadesDaOcorrencia, ordenarPorNome } from '../../services/listasApi';
import {
  chamarWebhookHelpdesk, atualizarTicket, registrarContatoSemSucesso,
  avisarComercialNoTeams, notificarEmAtendimento,
} from '../../services/helpdeskApi';
import {
  buscarIncidentes, retornosDoTicket, concluirIncidenteDoTicket,
} from '../../services/incidentesApi';
import {
  montarEnvio, montarInteracaoContato, tentativasIncompletas,
  aplicarDestinoSuporteAvancado, DESTINOS_SUPORTE_AVANCADO,
  TIPOS_FORMALIZACAO, TIPOS_SEM_PREVISAO, faltaPrevisaoDoAgendado,
} from '../../services/formalizacaoService';
import { encontrarMelhorMatch } from '../../services/texto';
import { formatarPrevisao } from '../../services/datas';
import { historico as historicoPlataforma } from '../../services/plataforma';
import { WEBHOOKS } from '../../config/ambiente';
import { Metric, EmptyState, FlowHint, PanelHead, StatusIncidentePill } from '../../components/ui';
import CampoAnotacoes from '../../components/CampoAnotacoes';
import {
  semMarcadores, ordenarPeloTexto, reancorarMarcadores, idsNoTexto,
} from '../../services/textoComAnexos';
import MatrizGut from '../../components/MatrizGut';
import TicketsEmMassa from '../../components/TicketsEmMassa';
import PosEnvio from '../../components/PosEnvio';
import TransicaoIncidente from '../../components/TransicaoIncidente';
import NovoTicket from '../../components/NovoTicket';
import FormalizacaoRapida from '../../components/FormalizacaoRapida';
import { CardFilaRetorno } from '../../components/FilaRetorno';
import ResumoTicket from '../../components/ResumoTicket';
import ObservacaoCliente from '../../components/ObservacaoCliente';
import {
  numeroDoTicket, assuntoDoTicket, clienteDoTicket, textoDoPrazo, prazoDoTicket,
} from '../../services/filaRetorno';
import { infoPrioridade, compararPrioridade } from '../../services/prioridade';
import Icon from '../../components/Icon';

/**
 * Ordem das filas da tela: prioridade (3, 2, 1) e, dentro da mesma
 * prioridade, a previsão (data e hora) mais próxima primeiro. Sem previsão vai
 * para o fim da sua prioridade.
 */
function ordenarPorPrioridade(tickets) {
  return [...tickets].sort((a, b) => {
    const porPrioridade = compararPrioridade(a, b);
    if (porPrioridade) return porPrioridade;
    const pa = prazoDoTicket(a);
    const pb = prazoDoTicket(b);
    if (pa && pb) return pa.getTime() - pb.getTime();
    if (pa) return -1;
    if (pb) return 1;
    return Number(numeroDoTicket(a) ?? a.codigo) - Number(numeroDoTicket(b) ?? b.codigo);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Formalização — a tela principal do Orion.
//
// Três fases, na ordem em que o atendimento acontece:
//   1. de quem é o ticket    → nº do ticket, solicitante, meus atendimentos;
//   2. o que aconteceu       → tipo, anotações, imagens, áudio, ocorrência;
//   3. o que vai ser enviado → texto formalizado, classificação, previsão.
//
// A marcação segue o vocabulário do Nexus: `panel` para os blocos, `sol-card`
// para os itens de lista, `field-label`/`fld-value` para os campos, `metrics`
// para a linha de números. É o mesmo desenho das telas de lá.
// ─────────────────────────────────────────────────────────────────────────────

const ESTADO_INICIAL_TENTATIVA = { telefone: '', motivo: '' };

/** Mínimo de caracteres para mandar as anotações sem passar pela IA. */
const MINIMO_SEM_FORMALIZAR = 50;

// ─────────────────────────────────────────────────────────────────────────────
// Os cartões da tela de início.
//
// Cada um é um recorte da fila do operador no MySuite — e é também o seletor
// da lista que aparece logo abaixo: clicou no cartão, a lista troca. O último,
// "Histórico", não vem do MySuite: vem do arquivo local de formalizações.
//
// "Aguardando solução" e "Outros status" somem quando estão zerados
// (`soQuandoTem`): aparecem pouco, e quando aparecem têm de saltar aos olhos —
// por isso vêm em tom de atenção. "Aguardando solução", quase sempre reabertura
// de atendimento, vem primeiro.
// ─────────────────────────────────────────────────────────────────────────────

// Filas vazias — a referência fixa que a tela usa enquanto o contexto não
// entregou nada. Constante fora do componente de propósito: um objeto novo a
// cada render faria os efeitos que dependem de `filas` rodarem sem motivo.
const VAZIO_FILAS = { aguardandoSolucao: [], emAtendimento: [], setorSistemas: [], outrosStatus: [] };

const LISTAS_MYSUITE = [
  {
    chave: 'aguardandoSolucao',
    icone: 'alert-circle',
    label: 'Aguardando solução',
    sub: 'status 1 no MySuite',
    pill: { texto: 'Aguardando solução', classe: 'st-aguardando-pill' },
    vazio: 'Nenhum ticket seu aguardando solução.',
    soQuandoTem: true,
    alerta: true,
  },
  {
    chave: 'emAtendimento',
    icone: 'inbox',
    label: 'Em atendimento com você',
    sub: 'status 36 no MySuite',
    pill: { texto: 'Em atendimento', classe: 'st-em_atendimento-pill' },
    vazio: 'Nenhum ticket em atendimento com você agora.',
  },
  {
    chave: 'filaRetorno',
    icone: 'clock',
    label: 'Sua fila de retorno',
    sub: 'status 5 no MySuite',
    pill: { texto: 'Fila de retorno', classe: 'st-recebida-pill' },
    vazio: 'Nenhum ticket seu esperando retorno.',
  },
  {
    chave: 'setorSistemas',
    icone: 'code',
    label: 'No setor de sistemas',
    sub: 'status 18 no MySuite',
    pill: { texto: 'Setor de sistemas', classe: 'st-sistemas-pill' },
    vazio: 'Nenhum ticket seu no Setor de Sistemas.',
  },
  // O que está no nome do operador e não cai em nenhum cartão acima — nem é
  // concluído, nem é do Setor de Sistemas (ver `ticketsEmOutrosStatus`). Sem
  // ele, um "Aguardando interação do cliente" ficava no limbo. Como o
  // "Aguardando solução", só aparece quando tem ticket, e em tom de atenção.
  // A etiqueta de cada ticket é o status dele — ver `pillDoStatus`.
  {
    chave: 'outrosStatus',
    icone: 'layers',
    label: 'Outros status',
    sub: 'no seu nome, fora dos cartões',
    pill: null,
    vazio: 'Nenhum ticket seu em outro status.',
    soQuandoTem: true,
    alerta: true,
  },
];

/** A etiqueta do cartão "Outros status": o status do próprio ticket, sem o "SUP - ". */
function pillDoStatus(ticket) {
  const texto = String(ticket?.status || '')
    .replace(/^sup\s*-\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return { texto: texto || 'Outro status', classe: 'st-aguardando-pill' };
}

/**
 * Um ticket da fila, no formato de card de lista do Nexus.
 *
 * Os campos saem pelos helpers de `filaRetorno`: a fila de pendentes devolve
 * `titulo`/`cliente` e a fila de retorno devolve `assunto`/`nomeCliente`. Um
 * card só lê os dois formatos.
 *
 * `extra` é a linha de contexto da fila — hoje, o prazo do retorno.
 */
function CardTicket({ ticket, pill, extra, aoAbrir }) {
  const codigo = numeroDoTicket(ticket) ?? ticket.codigo;
  const cliente = clienteDoTicket(ticket);
  // A barra lateral do card é a cor da prioridade (ver Orion.css, .prio-*).
  const prio = infoPrioridade(ticket);

  return (
    // O clique ABRE O RESUMO — não atende. Assumir um ticket é ação com efeito
    // no MySuite, e ninguém deve disparar isso de raspão numa lista.
    <div className={`sol-card ${prio?.classe || ''}`} onClick={() => aoAbrir(codigo, ticket)}>
      <div className="sol-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sol-meta">
            {prio && <span className={`prio-chip ${prio.classe}`}>{prio.rotulo}</span>}
            <span className={`pill ${pill.classe}`}>{pill.texto}</span>
            {extra && <span className="sol-sistema">{extra}</span>}
            {ticket.classificacao && <span className="sol-sistema">{ticket.classificacao}</span>}
          </div>
          <div className="sol-id">
            #{codigo}{cliente ? ` · ${cliente}` : ''}
            {ticket.dataAlteracao ? ` · alterado em ${ticket.dataAlteracao}` : ''}
          </div>
          <div className="sol-desc">{assuntoDoTicket(ticket)}</div>
        </div>
        <div className="sol-actions">
          <button
            className="btn btn-primary btn-sm"
            onClick={e => { e.stopPropagation(); aoAbrir(codigo, ticket); }}
          >
            Abrir <Icon name="chevron-right" size={12} />
          </button>

        </div>
      </div>
    </div>
  );
}

/** Trilha das três fases, no formato da trilha de etapas do Nexus. */
function TrilhaFases({ fase }) {
  const etapas = [
    { n: 1, label: 'Ticket' },
    { n: 2, label: 'Atendimento' },
    { n: 3, label: 'Revisão' },
  ];
  return (
    <div className="trilha trilha-compacta">
      {etapas.map((e, i) => (
        <React.Fragment key={e.n}>
          <div className={`trilha-node ${fase === e.n ? 'trilha-node-atual' : ''} ${fase > e.n ? 'trilha-node-tem' : 'trilha-node-off'}`}>
            <span className="trilha-bolha">{fase > e.n ? <Icon name="check" size={12} /> : e.n}</span>
            <span className="trilha-label">{e.label}</span>
          </div>
          {i < etapas.length - 1 && <span className="trilha-conector" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function Helpdesk() {
  const {
    operador, avisar, abrirRegistroIncidente, atividade, setTextoParaCopiar,
    formalizacaoParaReusar, setFormalizacaoParaReusar,
    filaRetorno, filasMysuite, ticketParaAbrir, setTicketParaAbrir,
    setView, reutilizarFormalizacao,
  } = useOrion();

  // ── Fase ──────────────────────────────────────────────────────────────────
  const [fase, setFase] = useState(1);

  // ── Fase 1 ────────────────────────────────────────────────────────────────
  const [numeroTicket, setNumeroTicket] = useState('');
  const [solicitante, setSolicitante] = useState('');
  const [buscandoTicket, setBuscandoTicket] = useState(false);
  const [ticketInfo, setTicketInfo] = useState(null);

  // Para qual ticket vale o nome que está no campo "Solicitante" — resolvido
  // pelo MySuite ou digitado à mão. Assim que o número em tela deixa de ser
  // este, o nome não vale mais e o efeito da seção de busca o apaga. Sem essa
  // amarração o solicitante de um atendimento seguia na tela e ia parar na
  // formalização do ticket seguinte.
  const solicitanteDoTicket = useRef('');

  // O ticket de onde veio o texto reaproveitado, quando o operador clicou em
  // "Reutilizar" no histórico. Serve para duas coisas: avisar na tela que falta
  // informar o novo número e RECUSAR esse mesmo número se ele for digitado de
  // volta — reutilizar é para outro atendimento, não para reescrever o mesmo.
  const [reusoDe, setReusoDe] = useState('');

  // As três filas do operador no MySuite. Qual delas aparece na lista de baixo
  // é decidido pelos cartões de número — eles são o seletor da lista.
  // 'historico' é a quarta opção: ali a lista vem do arquivo local, não do MySuite.
  //
  // A lista em si chega pronta do contexto: quem consulta o MySuite de 5 em 5
  // minutos é o `useFilasMysuite`, que roda desde o login em qualquer aba (ver
  // services/useFilasMysuite.js). Esta tela só exibe e pede recarga manual.
  const filas = filasMysuite?.filas || VAZIO_FILAS;
  const recarregarFilas = filasMysuite?.recarregar;
  const [listaAtiva, setListaAtiva] = useState('emAtendimento');

  // O botão "Atualizar". As filas leem uma tabela que o MySuite só recarrega
  // em ciclos (ver `confirmouEmAtendimento`): sem pedir a carga do dia antes,
  // atualizar devolvia a mesma foto. A carga responde na hora e corre em
  // segundo plano — por isso a leitura se repete alguns segundos depois, para
  // pegar o que ela trouxe.
  //
  // `atualizando` cobre o ciclo inteiro, e não só uma consulta: o ícone gira
  // até a segunda leitura voltar, que é quando os cartões param de mudar.
  const [atualizando, setAtualizando] = useState(false);
  const atualizarFilas = async () => {
    if (atualizando) return;
    setAtualizando(true);
    try {
      await dispararCargaDoDia();
      const recarregar = () => Promise.all([recarregarFilas?.(), filaRetorno?.recarregar?.()]);
      await recarregar();
      await new Promise(r => setTimeout(r, 5000));
      await recarregar();
    } finally {
      setAtualizando(false);
    }
  };
  // O relógio de 5 min e a volta do foco também consultam: girar junto avisa
  // que os números podem mudar a qualquer instante.
  const filasCarregando = atualizando || !!filasMysuite?.carregando || !!filaRetorno?.carregando;

  // O resumo aberto: { numero, ticket }. É o que todo card abre agora — as
  // ações (formalizar, atender, ver incidente, interações) moram lá dentro.
  const [resumo, setResumo] = useState(null);

  // ── Fase 2 ────────────────────────────────────────────────────────────────
  const [tipo, setTipo] = useState('');
  const [anotacoes, setAnotacoes] = useState('');
  const [imagens, setImagens] = useState([]);
  const [audio, setAudio] = useState(null);
  const [possivelFalha, setPossivelFalha] = useState(false);
  const [tentativas, setTentativas] = useState([{ ...ESTADO_INICIAL_TENTATIVA }]);

  const [listas, setListas] = useState({
    ocorrencias: [], criticidades: [], ocorrenciaCriticidades: [], classificacoes: [],
  });
  const [ocorrencia, setOcorrencia] = useState('');
  const [criticidade, setCriticidade] = useState('');

  // ── Fase 3 ────────────────────────────────────────────────────────────────
  const [textoFormalizado, setTextoFormalizado] = useState('');
  const [sql, setSql] = useState('');
  const [assuntoIa, setAssuntoIa] = useState('');
  const [classificacoesIa, setClassificacoesIa] = useState(null);
  const [classificandoIa, setClassificandoIa] = useState(false);
  const [iaAplicada, setIaAplicada] = useState(false);
  const [ajusteAberto, setAjusteAberto] = useState(false);
  const [ajusteAssunto, setAjusteAssunto] = useState('');
  const [ajusteClassificacao, setAjusteClassificacao] = useState('');
  const [previsaoData, setPrevisaoData] = useState('');
  const [previsaoHora, setPrevisaoHora] = useState('');
  // O texto da fase 3 é o que o operador escreveu, sem passar pela IA.
  const [semFormalizacao, setSemFormalizacao] = useState(false);
  // A interação vai como PRIVADA no MySuite: só a equipe vê, o cliente não.
  const [interacaoPrivada, setInteracaoPrivada] = useState(false);

  // Todo Suporte avançado vai para um analista nominal, marcado como possível
  // falha de sistema ou não. A escolha é feita na confirmação do envio e zera a
  // cada revisão, para o operador decidir sempre.
  const [destinoSupAvancado, setDestinoSupAvancado] = useState('');

  const [formalizando, setFormalizando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [validacao, setValidacao] = useState(null);
  const [idHistorico, setIdHistorico] = useState(null);

  // GUT: só entra em jogo no Suporte avançado com falha de sistema. Cada envio
  // classifica o seu próprio — por isso volta a 1×1×1 ao recomeçar.
  // `justificativa` é o texto que a IA devolve junto da sugestão: ele viaja com
  // o GUT até o incidente (webhook salvar-erro), como no 1.4.1.
  const [gut, setGut] = useState({ g: 1, u: 1, t: 1, justificativa: '' });

  // Criação em massa: o mesmo problema atingindo vários clientes. Abre a
  // partir da revisão, aproveitando o que já foi escrito e classificado.
  const [massaAberta, setMassaAberta] = useState(false);

  // Ticket novo derivado deste atendimento (o cliente trouxe outro caso).
  const [novoTicketAberto, setNovoTicketAberto] = useState(false);

  // Formalização rápida: texto avulso, sem ticket. Vive fora das fases porque
  // não faz parte do fluxo — é um atalho lateral, aberto e fechado sozinho.
  const [rapidaAberta, setRapidaAberta] = useState(false);

  // Depois do envio a tela não fecha: oferece o próximo passo conforme o tipo
  // (registrar incidente, gerar documento) e lembra o que não pode faltar.
  const [posEnvio, setPosEnvio] = useState(null);

  // Ponte do atendimento para o incidente: a IA lê o que foi escrito e os
  // prints da formalização viram os prints do incidente. Enquanto isso roda,
  // o modal mostra em que passo está.
  const [transicao, setTransicao] = useState(null);

  // Incidentes parecidos com o que está sendo escrito. Aparecem enquanto o
  // operador digita as anotações, para ele descobrir que o erro já é
  // conhecido ANTES de terminar o atendimento.
  const [semelhantes, setSemelhantes] = useState([]);
  const buscaSemelhantes = useRef(null);


  // As formalizações guardadas neste computador. Alimentam o cartão "Histórico"
  // e a lista que ele abre.
  const [historicoLocal, setHistoricoLocal] = useState([]);

  const contatoSemSucesso = tipo === 'Contato sem sucesso';

  useEffect(() => {
    carregarListas()
      .then(setListas)
      .catch(err => avisar(`Falha ao carregar as listas: ${err.message}`, { tipo: 'erro' }));
  }, [avisar]);

  // Voltar para a tela de início pede o quadro de agora. É barato: se o relógio
  // do contexto acabou de consultar, a resposta vem do mesmo POST que ele já
  // faria — e a trava de reentrada do hook impede consulta em duplicata.
  useEffect(() => { if (fase === 1) recarregarFilas?.(); }, [fase, recarregarFilas]);

  // Escrever o atendimento (fases 2 e 3) conta como atender, mesmo que o ticket
  // ainda não apareça em status 36 no MySuite. Sem isto, o contador de
  // ociosidade corria durante o atendimento e disparava logo após finalizar.
  useEffect(() => {
    atividade?.registrarAtendimento?.(fase >= 2);
  }, [fase, atividade]);

  // O histórico local, para o cartão "Histórico" e a lista que ele abre.
  useEffect(() => {
    if (fase !== 1) return undefined;
    let vivo = true;
    historicoPlataforma.carregar()
      .then(lista => { if (vivo) setHistoricoLocal(lista || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [fase]);

  // "Aguardando solução" some da linha quando zera. Se era ele que estava
  // aberto, a lista volta para os atendimentos — senão ficaria uma lista vazia
  // sem nenhum cartão marcado, e sem como sair dela.
  useEffect(() => {
    const l = LISTAS_MYSUITE.find(x => x.chave === listaAtiva);
    if (l?.soQuandoTem && !(filas[listaAtiva] || []).length) setListaAtiva('emAtendimento');
  }, [filas, listaAtiva]);

  // Espera de 900ms: a busca por significado roda no servidor, e disparar a
  // cada tecla seria uma consulta por caractere.
  useEffect(() => {
    const texto = anotacoes.trim();
    clearTimeout(buscaSemelhantes.current);
    if (fase !== 2 || texto.length < 25 || contatoSemSucesso) { setSemelhantes([]); return undefined; }

    buscaSemelhantes.current = setTimeout(async () => {
      try {
        const { lista } = await buscarIncidentes(texto, {});
        setSemelhantes((lista || []).slice(0, 3));
      } catch (e) { setSemelhantes([]); }
    }, 900);

    return () => clearTimeout(buscaSemelhantes.current);
  }, [anotacoes, fase, contatoSemSucesso]);

  // Veio do histórico pelo 'Reutilizar': o TEXTO volta, o TICKET não.
  //
  // Reutilizar é para o mesmo problema em outro atendimento — o texto já
  // escrito serve, o número não. Antes o ticket de origem vinha junto e ficava
  // preenchido: bastava seguir em frente para escrever de novo, e enviar, no
  // ticket que já tinha sido atendido.
  //
  // Agora a tela volta para a fase 1 com o campo do ticket VAZIO. O operador
  // informa o novo número, ele é buscado no MySuite como qualquer outro, e só
  // então o atendimento segue. `reusoDe` guarda o número de origem apenas para
  // recusá-lo se for digitado de volta.
  useEffect(() => {
    if (!formalizacaoParaReusar) return;
    const e = formalizacaoParaReusar;

    // O que se aproveita: o que foi escrito e como foi classificado.
    setTipo(e.tipo_atendimento || '');
    setAnotacoes(e.anotacoes || '');
    setTextoFormalizado(e.ticket || '');
    setAjusteAssunto(e.assunto || '');
    setAjusteClassificacao(e.classificacao || '');

    // O que NÃO se aproveita: o ticket e quem o abriu.
    setNumeroTicket('');
    setSolicitante('');
    solicitanteDoTicket.current = '';
    setTicketInfo(null);
    setReusoDe(String(e.numero_ticket || ''));

    setFase(1);
    setFormalizacaoParaReusar(null);
    avisar(
      'Texto do atendimento carregado. Informe o nº do NOVO ticket — o texto será reaproveitado nele.',
      { tipo: 'info', titulo: 'Reutilizando um atendimento', duracao: 9000 },
    );
  }, [formalizacaoParaReusar, setFormalizacaoParaReusar, avisar]);

  const opcoesCriticidade = criticidadesDaOcorrencia(listas, ocorrencia);

  // Clicar num card abre o RESUMO do ticket, e nada mais. Vale para qualquer
  // fila: o que muda de uma para outra são as ações que o resumo oferece.
  //
  // Antes, o clique assumia o ticket no MySuite e já abria a formalização. Era
  // muita coisa para um clique numa lista — e um clique errado marcava um
  // ticket em nome de quem não ia atendê-lo.
  //
  // `ticket` é o registro que a lista já tem em mãos: o resumo usa para pintar
  // cliente e assunto enquanto as consultas não voltam.
  const abrirResumo = (numero, ticket = null) => {
    if (numero == null) return;
    setResumo({ numero: String(numero), ticket });
  };

  // O Ctrl+E copia o ticket formalizado de qualquer lugar do app; para isso o
  // texto precisa estar visível fora desta tela.
  useEffect(() => {
    setTextoParaCopiar(semMarcadores(textoFormalizado));
  }, [textoFormalizado, setTextoParaCopiar]);

  // O texto formalizado guarda os marcadores das imagens, que só fazem sentido
  // na tela e no documento. Tudo o que sai daqui para fora — MySuite, histórico,
  // IA, ticket em massa — leva a versão sem eles.
  const textoLimpo = semMarcadores(textoFormalizado);

  // Quem definiu a classificação que vai junto do ticket. A mesma ordem de
  // prioridade que o formalizacaoService usa: ajuste manual > IA > MySuite.
  const origemClassificacao = (() => {
    if (ajusteAssunto || ajusteClassificacao) return { rotulo: 'Ajuste manual', classe: 'd-info' };
    if (iaAplicada) return { rotulo: 'Recomendação da IA', classe: 'd-ia' };
    return { rotulo: 'Classificação do MySuite', classe: 'd-atual' };
  })();

  // Ocorrência com uma única criticidade vinculada preenche sozinha.
  useEffect(() => {
    if (opcoesCriticidade.lista.length === 1) setCriticidade(String(opcoesCriticidade.lista[0].codigo));
    else setCriticidade('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocorrencia]);

  // ── Busca do ticket ───────────────────────────────────────────────────────

  // Guarda o nome e a quem ele pertence sempre juntos: é o par que o efeito
  // abaixo confere.
  const anotarSolicitante = (nome, numero) => {
    setSolicitante(nome);
    solicitanteDoTicket.current = nome ? String(numero).trim() : '';
  };

  // O número mudou: o que está no campo é de outro ticket e sai da tela. Vale
  // para toda forma de mudar o número — digitação, card, ticket recém-criado.
  useEffect(() => {
    if (numeroTicket.trim() === solicitanteDoTicket.current) return;
    solicitanteDoTicket.current = '';
    setSolicitante('');
    setTicketInfo(null);
  }, [numeroTicket]);

  // Quem abriu o ticket, segundo o MySuite. As chamadas por baixo engolem a
  // falha e devolvem vazio; aqui o vazio é dito ao operador, porque campo em
  // branco é honesto e o nome do atendimento anterior não é.
  const buscarSolicitanteDe = async (num) => {
    const nome = await resolverSolicitantePorTicket(num);
    if (!nome) {
      avisar(
        `Não foi possível descobrir quem abriu o ticket ${num}. Confira o número e informe o solicitante à mão antes de formalizar.`,
        { tipo: 'erro', titulo: 'Solicitante não resolvido', duracao: 9000 },
      );
    }
    return nome;
  };

  const buscar = async (numero) => {
    const num = String(numero ?? numeroTicket).trim();
    if (!num) { avisar('Informe o nº do ticket.', { tipo: 'erro' }); return; }

    // Reutilizar é aproveitar o TEXTO em outro atendimento. Voltar ao ticket de
    // origem escreveria de novo num chamado já atendido — e é justamente o que
    // o botão parecia oferecer antes.
    if (reusoDe && num === reusoDe) {
      avisar(
        `O texto veio do ticket ${reusoDe}. Informe o número de OUTRO ticket para reaproveitá-lo.`,
        { tipo: 'erro', titulo: 'Este é o ticket de origem', duracao: 9000 },
      );
      return;
    }

    setNumeroTicket(num);

    // O que já estava em tela só continua se for DESTE ticket (o operador
    // pode tê-lo digitado à mão). De outro ticket, sai agora, aqui mesmo.
    const herdado = solicitanteDoTicket.current === num ? solicitante.trim() : '';
    if (!herdado) { setSolicitante(''); setTicketInfo(null); }
    // A amarração passa a apontar para o ticket que está sendo carregado, de
    // modo que o efeito acima seja indiferente à ordem em que rodar: a partir
    // daqui quem manda no campo é esta busca, e ela o reescreve no fim.
    solicitanteDoTicket.current = num;

    setBuscandoTicket(true);
    try {
      const dados = await buscarTicket(num);
      setTicketInfo(dados);

      if (!herdado) {
        anotarSolicitante(await buscarSolicitanteDe(num), num);
      }

      setPrevisaoData(dados.dataPrevisao && dados.dataPrevisao !== '0000-00-00' ? dados.dataPrevisao : '');
      setPrevisaoHora(dados.horaPrevisao && dados.horaPrevisao !== '00:00:00' ? dados.horaPrevisao.substring(0, 5) : '');

      // Ocorrência e criticidade do MySuite vêm como texto; casa com a lista.
      if (dados.ocorrencia) {
        const m = encontrarMelhorMatch(dados.ocorrencia, listas.ocorrencias.map(o => ({ valor: String(o.codigo), texto: o.nome })));
        if (m) setOcorrencia(m.valor);
      }
      if (dados.criticidade) {
        const m = encontrarMelhorMatch(dados.criticidade, listas.criticidades.map(c => ({ valor: String(c.codigo), texto: c.nome })));
        if (m) setCriticidade(m.valor);
      }

      setFase(2);

      // A notificação de "em atendimento" NÃO sai aqui: no 1.4.1 ela acontece
      // depois de atuar no ticket (ver confirmarEnvio), e leva a fila inteira
      // do operador, não o ticket que ele abriu.

      // O ticket pode estar com outro colega: avisa, mas não bloqueia.
      const dono = await verificarDonoDoTicket(num, operador?.codigo, dados);
      if (dono.deOutro) {
        avisar(
          `O ticket ${num} está em atendimento com ${dono.nomeDono || `o operador ${dono.dono}`}, não com você. `
          + 'Confira antes de seguir para não atuar em um atendimento de outra pessoa.',
          { tipo: 'erro', titulo: 'Ticket de outro operador', duracao: 12000 }
        );
      }

      // ESTE TICKET VOLTOU DO SETOR DE SISTEMAS.
      //
      // A correção foi feita, o suporte avançado devolveu o chamado e ele caiu
      // na fila de retorno como qualquer agendamento — sem dizer o motivo. O
      // aviso é esse motivo: não é um retorno comum, é uma atualização para
      // agendar, e a versão é o que o cliente precisa ouvir.
      //
      // Melhor esforço: a consulta é acessória e não pode atrapalhar a busca.
      try {
        const devolvidos = await retornosDoTicket(num);
        if (devolvidos.length) {
          const versoes = [...new Set(devolvidos.map(i => i.nexus_versao).filter(Boolean))];
          avisar(
            'A correção deste atendimento está disponível'
            + (versoes.length ? ` na versão ${versoes.join(' / ')}` : '')
            + '. Por gentileza, entre em contato com o cliente para agendar a atualização do '
            + 'sistema — e, ao concluir aqui, o setor de Sistemas é avisado automaticamente.',
            { tipo: 'ok', titulo: 'Voltou do setor de Sistemas', duracao: 15000 },
          );
        }
      } catch (e) {
        console.warn('[Orion] Retorno do setor de Sistemas:', e.message);
      }
    } catch (err) {
      avisar(`Erro ao buscar ticket: ${err.message}`, { tipo: 'erro' });
    } finally {
      setBuscandoTicket(false);
    }
  };

  // Pedido vindo de outra tela: o card ou o aviso da fila de retorno mandaram
  // abrir um ticket. O ref evita listar `buscar` como dependência do efeito —
  // ela é recriada a cada render e dispararia a busca em loop.
  const buscarRef = useRef(buscar);
  buscarRef.current = buscar;

  useEffect(() => {
    if (!ticketParaAbrir) return;
    const numero = ticketParaAbrir;
    // Limpa antes de buscar: se a busca falhar, o operador tenta de novo pela
    // tela em vez de o pedido ficar preso e reabrir sozinho.
    setTicketParaAbrir(null);
    buscarRef.current(numero);
  }, [ticketParaAbrir, setTicketParaAbrir]);

  const resolverSolicitante = async () => {
    const num = numeroTicket.trim();
    if (!num) return;
    // Já resolvido — ou digitado à mão — para ESTE ticket: nada a refazer.
    if (solicitanteDoTicket.current === num && solicitante.trim()) return;
    anotarSolicitante(await buscarSolicitanteDe(num), num);
  };

  // ── Formalização (fase 2 → 3) ─────────────────────────────────────────────

  /** O que formalizar e enviar sem formalizar cobram igual. Devolve o nº ou null. */
  const conferirCabecalho = () => {
    const num = numeroTicket.trim();
    if (!num) { avisar('O nº do ticket é obrigatório.', { tipo: 'erro' }); return null; }
    // A mesma trava da busca, repetida aqui de propósito: é a última porta
    // antes de o texto reaproveitado virar atendimento.
    if (reusoDe && num === reusoDe) {
      avisar(
        `Este texto veio do ticket ${reusoDe}. Reutilizar exige outro ticket.`,
        { tipo: 'erro', titulo: 'Este é o ticket de origem' },
      );
      return null;
    }
    if (!solicitante.trim()) { avisar('O solicitante é obrigatório.', { tipo: 'erro' }); return null; }
    if (!tipo) { avisar('Selecione o tipo de atendimento.', { tipo: 'erro' }); return null; }
    // Cobrado já aqui, e não só no envio: é na fase 2 que os campos aparecem.
    const faltaAgenda = faltaPrevisaoDoAgendado(tipo, previsaoData, previsaoHora);
    if (faltaAgenda) { avisar(faltaAgenda, { tipo: 'erro' }); return null; }
    return num;
  };

  // Sem a IA, o texto vai como foi escrito — e por isso precisa ter corpo:
  // "resolvido" ou "liguei pro cliente" não registram atendimento nenhum.
  const tamanhoSemFormalizar = semMarcadores(anotacoes).trim().length;
  const podeSemFormalizar = tamanhoSemFormalizar >= MINIMO_SEM_FORMALIZAR;

  /** Fase 2 → 3 sem passar pela IA: o texto digitado é o que vai para o MySuite. */
  const enviarSemFormalizar = () => {
    const num = conferirCabecalho();
    if (!num) return;
    if (!podeSemFormalizar) {
      avisar(
        `Para enviar sem formalizar, escreva ao menos ${MINIMO_SEM_FORMALIZAR} caracteres (hoje: ${tamanhoSemFormalizar}).`,
        { tipo: 'erro' },
      );
      return;
    }

    const id = String(Date.now());
    setIdHistorico(id);
    // Os marcadores das imagens ficam: o texto é o mesmo, e elas continuam
    // onde o operador as colou.
    setTextoFormalizado(anotacoes);
    setSql('');
    setAssuntoIa('');
    setClassificacoesIa(null);
    setIaAplicada(false);
    setSemFormalizacao(true);
    setFase(3);

    historicoPlataforma.salvarEntrada({
      id,
      data: new Date().toLocaleString('pt-BR'),
      numero_ticket: num,
      solicitante,
      tipo_atendimento: tipo,
      anotacoes: anotacoes.trim(),
      ticket: semMarcadores(anotacoes).trim(),
      assunto: '',
      classificacao: '',
      ocorrencia: '',
      criticidade: '',
    });

    avisar('Texto mantido como você escreveu. Revise e envie.', { tipo: 'ok' });
  };

  const formalizar = async () => {
    const num = conferirCabecalho();
    if (!num) return;
    setSemFormalizacao(false);

    const id = String(Date.now());
    setIdHistorico(id);

    // ── Contato sem sucesso: atalho sem IA ──
    if (contatoSemSucesso) {
      if (tentativasIncompletas(tentativas)) {
        avisar('Cada tentativa precisa de telefone e do que ocorreu.', { tipo: 'erro' });
        return;
      }
      const preenchidas = tentativas.filter(t => t.telefone.trim() && t.motivo.trim());
      if (!preenchidas.length) {
        avisar('Informe ao menos uma tentativa (telefone + o que ocorreu).', { tipo: 'erro' });
        return;
      }

      const texto = montarInteracaoContato(tentativas);
      setTextoFormalizado(texto);
      setSql('');
      setFase(3);

      historicoPlataforma.salvarEntrada({
        id,
        data: new Date().toLocaleString('pt-BR'),
        numero_ticket: num,
        solicitante,
        tipo_atendimento: 'Contato sem sucesso',
        anotacoes: texto,
        ticket: texto,
        assunto: '',
        classificacao: 'OUT - Contato Não Estabelecido',
        ocorrencia: '',
        criticidade: '',
      });

      avisar('Tentativa registrada. Revise e envie.', { tipo: 'ok' });
      return;
    }

    // Anotações obrigatórias, EXCETO quando há áudio — nesse caso é o áudio
    // que alimenta a formalização.
    if (!anotacoes.trim() && !audio) {
      avisar('Informe as anotações ou anexe um áudio.', { tipo: 'erro' });
      return;
    }

    setFormalizando(true);

    // As imagens vão na ORDEM EM QUE APARECEM NO TEXTO, não na ordem em que
    // foram anexadas — é essa a sequência que o operador escreveu e a que o
    // documento tem de respeitar. O que ficou fora do texto vai no fim.
    const imagensOrdenadas = ordenarPeloTexto(anotacoes, imagens);

    const payload = {
      numero_ticket: num,
      solicitante,
      tipo_atendimento: tipo,
      // Sem os marcadores: a IA não os entende e devolveria `[[anexo:` picado
      // no meio de uma frase. Onde cada imagem estava é reposto na volta.
      anotacoes: semMarcadores(anotacoes),
      operador_nome: operador?.nome || '',
      operador_email: operador?.email || '',
      tem_audio: !!audio,
      // `nome` é o nome do ARQUIVO, como no 1.4.1 — o nome montado a partir
      // da legenda pertence ao envio do ticket (OrionAtualiza), não a este
      // fluxo. Trocar um pelo outro muda o que o n8n recebe.
      ...(imagensOrdenadas.length > 0
        ? { imagens: imagensOrdenadas.map(img => ({ base64: img.base64, nome: img.nome, legenda: img.legenda })) }
        : {}),
    };

    let texto = '';
    let idSupabase = null;

    try {
      const resp = await chamarWebhookHelpdesk(
        payload, WEBHOOKS.helpdesk,
        audio ? audio.blob : null, audio?.nome
      );
      const r = Array.isArray(resp) ? resp[0] : resp;

      texto = r.ticket_texto || r.texto_final || r.ticket || r.texto || r.text || r.output || '';
      if (!texto && typeof r === 'string') texto = r;
      texto = texto.replace(/<br\s*\/?>/gi, '\n');
      idSupabase = r.id || null;

      // Bloco SQL sai do corpo e ganha painel próprio — misturado ao texto do
      // ticket, ele polui a leitura.
      const regexSql = /```sql([\s\S]*?)```/i;
      const achado = texto.match(regexSql);
      if (achado) {
        setSql(achado[1].trim());
        texto = texto.replace(regexSql, '\n\n[Código SQL extraído e listado abaixo]').trim();
      } else {
        setSql('');
      }

      // As imagens voltam para o texto formalizado, na mesma ORDEM e mais ou
      // menos no mesmo ponto do relato. O ponto é aproximado — o texto foi
      // reescrito pela IA —, e é por isso que a fase 3 é editável: dá para
      // arrastar a imagem de parágrafo antes de gerar o documento.
      setTextoFormalizado(reancorarMarcadores(anotacoes, texto));
      setAssuntoIa('');
      setClassificacoesIa(null);
      setIaAplicada(false);
      setFase(3);

      historicoPlataforma.salvarEntrada({
        id,
        data: new Date().toLocaleString('pt-BR'),
        numero_ticket: num,
        solicitante,
        tipo_atendimento: tipo,
        anotacoes: anotacoes.trim(),
        ticket: texto,
        assunto: '',
        classificacao: '',
        ocorrencia: '',
        criticidade: '',
      });
    } catch (err) {
      avisar(`Erro na formalização: ${err.message}`, { tipo: 'erro' });
      setFormalizando(false);
      return;
    }

    setFormalizando(false);

    // Classificação em segundo plano: o operador já revisa o texto enquanto a
    // IA classifica. Travar a tela aqui custaria segundos a cada atendimento.
    setClassificandoIa(true);
    chamarWebhookHelpdesk(
      { ...payload, texto_final: texto, ticket_id: idSupabase },
      WEBHOOKS.classificacao || WEBHOOKS.helpdesk
    )
      .then(resp => {
        const r = Array.isArray(resp) ? resp[0] : resp;
        const assunto = r.assunto || r.subject || '';
        setAssuntoIa(assunto);

        let classificacoes;
        if (r.classificacao || r.ocorrencia || r.criticidade) {
          classificacoes = {
            classificacao: r.classificacao || null,
            ocorrencia: r.ocorrencia || null,
            criticidade: r.criticidade || null,
            codigoClassificacao: r.codigo_classificacao || r.codigoClassificacao || null,
          };
        } else {
          classificacoes = r.classificacoes || r.classifications || null;
          if (classificacoes) {
            classificacoes.codigoClassificacao = r.codigo_classificacao
              || r.codigoClassificacao || classificacoes.codigoClassificacao || null;
          }
        }
        setClassificacoesIa(classificacoes);

        historicoPlataforma.salvarEntrada({
          id,
          assunto,
          classificacao: classificacoes?.classificacao || '',
          ocorrencia: classificacoes?.ocorrencia || '',
          criticidade: classificacoes?.criticidade || '',
        });
      })
      .catch(err => avisar(`Erro na classificação: ${err.message}`, { tipo: 'erro' }))
      .finally(() => setClassificandoIa(false));
  };

  const aplicarIa = () => {
    if (!classificacoesIa) return;
    setIaAplicada(true);

    const assunto = assuntoIa?.trim();
    if (assunto) setAjusteAssunto(assunto);

    if (classificacoesIa.ocorrencia) {
      const m = encontrarMelhorMatch(
        classificacoesIa.ocorrencia,
        listas.ocorrencias.map(o => ({ valor: String(o.codigo), texto: o.nome }))
      );
      if (m) setOcorrencia(m.valor);
    }
    if (classificacoesIa.criticidade) {
      const m = encontrarMelhorMatch(
        classificacoesIa.criticidade,
        listas.criticidades.map(c => ({ valor: String(c.codigo), texto: c.nome }))
      );
      if (m) setCriticidade(m.valor);
    }
    if (classificacoesIa.classificacao) setAjusteClassificacao(classificacoesIa.classificacao);

    avisar('Recomendação da IA aplicada.', { tipo: 'ok' });
  };

  // ── Envio (fase 3) ────────────────────────────────────────────────────────

  const revisar = () => {
    const num = numeroTicket.trim();
    if (!num) { avisar('Preencha o nº do ticket para enviar.', { tipo: 'erro' }); return; }

    // Falha de sistema vai para o Setor de Sistemas, e lá a fila é ordenada
    // pelo GUT. Enviar com 1×1×1 é o mesmo que não classificar.
    if (tipo === 'Suporte avançado' && possivelFalha && gut.g * gut.u * gut.t <= 1) {
      avisar('Classifique o GUT antes de enviar — é ele que ordena a fila do Setor de Sistemas.', { tipo: 'erro' });
      return;
    }
    // De novo no envio: a previsão pode ter sido apagada na fase 3, ou o
    // horário combinado pode ter passado enquanto o texto era revisado.
    const faltaAgenda = faltaPrevisaoDoAgendado(tipo, previsaoData, previsaoHora);
    if (faltaAgenda) { avisar(faltaAgenda, { tipo: 'erro' }); return; }

    setDestinoSupAvancado('');
    setValidacao(montarEnvio({
      numeroTicket: num,
      tipoAtendimento: tipo,
      interacao: textoLimpo || semMarcadores(anotacoes) || 'Interação vazia.',
      ticketInfo,
      operador,
      ocorrenciaSelecionada: ocorrencia,
      criticidadeSelecionada: criticidade,
      ajusteAssunto,
      ajusteClassificacao,
      previsaoData,
      previsaoHora,
      classificacoesIa,
      iaAplicada,
      listas,
    }));
  };

  const confirmarEnvio = async () => {
    if (!validacao || enviando) return;
    // Sem analista escolhido o ticket cairia na fila genérica, e o operador
    // acharia que encaminhou para alguém.
    if (precisaDestino && !destinoSupAvancado) {
      avisar('Escolha para quem encaminhar o Suporte avançado.', { tipo: 'erro' });
      return;
    }

    setEnviando(true);
    try {
      // O payload vai como está: no 1.4.1 o GUT NÃO acompanha o envio do
      // ticket — ele viaja com o incidente, na webhook salvar-erro. Mandar
      // gut_* aqui é campo a mais para o fluxo.
      //
      // As imagens vão junto, como binário no multipart. É isto que o fluxo
      // espera desde o 1.4.1; sem elas o ticket chega sem os prints.
      // `privado` vai sempre, 1 ou 0: o OrionAtualiza grava a interação como
      // privada no MySuite quando vem 1.
      const payloadFinal = {
        ...(precisaDestino
          ? aplicarDestinoSuporteAvancado(validacao.payload, destinoSupAvancado)
          : validacao.payload),
        privado: interacaoPrivada ? 1 : 0,
      };

      if (contatoSemSucesso) await registrarContatoSemSucesso(payloadFinal, imagens);
      else await atualizarTicket(payloadFinal, imagens);

      // O Comercial é avisado no Teams quando o atendimento vai para lá.
      if (tipo === 'Comercial') avisarComercialNoTeams([validacao.payload.codigoTicket]);

      // CONCLUIR AQUI ENCERRA A DEMANDA DO SETOR DE SISTEMAS.
      //
      // Se este ticket voltou de lá com a correção pronta, o atendimento que o
      // operador acabou de fechar É o fim da história: o cliente foi atualizado
      // e confirmou. O incidente é concluído no Pulse neste instante, e um
      // gatilho do banco fecha a solicitação no Nexus — sem o operador precisar
      // abrir outro sistema para dizer que terminou.
      //
      // Depois do envio, de propósito: o ticket já foi atualizado e nada aqui
      // pode desfazer isso. Uma falha vira aviso, não erro de envio.
      if (tipo === 'Conclusão') {
        try {
          const r = await concluirIncidenteDoTicket(validacao.payload.codigoTicket, {
            operadorNome: operador?.nome || '',
            solucao: textoLimpo,
          });
          if (r.concluidos) {
            avisar(
              `A demanda do setor de Sistemas foi encerrada (${r.concluidos === 1 ? '1 incidente' : `${r.concluidos} incidentes`}).`,
              { tipo: 'ok', titulo: 'Setor de Sistemas avisado' },
            );
          }
          if (r.falhas) {
            avisar(
              'O atendimento foi enviado, mas não foi possível encerrar a demanda no setor de '
              + 'Sistemas. Avise o suporte avançado para fechá-la no Pulse.',
              { tipo: 'erro', titulo: 'Confira no Pulse', duracao: 12000 },
            );
          }
        } catch (e) {
          console.warn('[Orion] Conclusão da demanda de Sistemas:', e.message);
        }
      }

      // Depois de atuar, o n8n recebe a fila do operador SEM este ticket.
      // Dispara e esquece, como no 1.4.1.
      ticketsEmAtendimentoDoOperador(operador?.codigo)
        .then(lista => notificarEmAtendimento({
          ticketAtuado: payloadFinal.codigoTicket,
          tickets: lista,
          operadorNome: operador?.nome || '',
          operadorEmail: operador?.email || '',
          codigoOperador: operador?.codigo ?? null,
          solicitante,
        }))
        .catch(e => console.warn('[Orion] Em atendimento:', e.message));

      historicoPlataforma.salvarEntrada({
        id: idHistorico,
        ticket: textoLimpo,
        assunto: validacao.resumo.assunto,
        classificacao: validacao.resumo.classificacao,
        ocorrencia: validacao.resumo.ocorrencia,
        criticidade: validacao.resumo.criticidade,
      });

      avisar(`Ticket ${validacao.payload.codigoTicket} enviado.`, { tipo: 'ok' });
      setValidacao(null);
      // A tela de confirmação dá lugar ao próximo passo — quem conclui é o
      // operador, no botão. Tipos sem passo seguinte já limpam a tela.
      const temPasso = ['Conclusão', 'Setor de sistemas', 'Suporte avançado', 'Comercial'].includes(tipo);
      // O resumo vai junto porque `validacao` acabou de ser limpa, e o
      // documento precisa da classificação que foi realmente enviada.
      if (temPasso) {
        setPosEnvio({
          tipo,
          numeroTicket: validacao.payload.codigoTicket,
          resumo: validacao.resumo,
        });
      }
      else reiniciar();
    } catch (err) {
      avisar(`Erro ao enviar: ${err.message}`, { tipo: 'erro' });
    } finally {
      setEnviando(false);
    }
  };

  const reiniciar = () => {
    setFase(1);
    setNumeroTicket('');
    setSolicitante('');
    solicitanteDoTicket.current = '';
    setTicketInfo(null);
    setTipo('');
    setAnotacoes('');
    setImagens([]);
    setAudio(null);
    setPossivelFalha(false);
    setTentativas([{ ...ESTADO_INICIAL_TENTATIVA }]);
    setOcorrencia('');
    setCriticidade('');
    setTextoFormalizado('');
    setSql('');
    setAssuntoIa('');
    setClassificacoesIa(null);
    setIaAplicada(false);
    setAjusteAberto(false);
    setAjusteAssunto('');
    setAjusteClassificacao('');
    setPrevisaoData('');
    setPrevisaoHora('');
    setSemFormalizacao(false);
    setInteracaoPrivada(false);                        // privada é escolha de cada envio
    setIdHistorico(null);
    setReusoDe('');                                    // a reutilização termina junto com o atendimento
    setGut({ g: 1, u: 1, t: 1, justificativa: '' });   // cada envio classifica o seu próprio GUT
  };

  const mostrarPrevisao = !TIPOS_SEM_PREVISAO.includes(tipo);

  // Todo Suporte avançado vai para um analista nominal — com ou sem "possível
  // falha de sistema". A marcação muda o que acompanha o ticket (GUT e o
  // incidente), não para quem ele vai: sem escolher, cairia na fila genérica.
  const precisaDestino = tipo === 'Suporte avançado';

  // O resumo do ticket, montado uma vez e usado nas três fases: ele é a única
  // tela de leitura de ticket do Orion — dados, situação e a conversa inteira.
  const resumoTicket = resumo && (
    <ResumoTicket
      numero={resumo.numero}
      ticketDaLista={resumo.ticket}
      aoFechar={() => setResumo(null)}
    />
  );

  // ── O que a tela de início mostra ─────────────────────────────────────────
  // A fila de retorno já tem fonte própria nesta tela — a do card com os
  // prazos (useFilaRetorno, por /tickets/filtro). O cartão usa a MESMA lista:
  // dois números diferentes para a mesma fila, lado a lado, seria pior do que
  // não ter o cartão.
  const listasDaTela = { ...filas, filaRetorno: filaRetorno?.tickets || [] };

  // "Aguardando solução" só ocupa lugar quando tem ticket — ver LISTAS_MYSUITE.
  const cartoesVisiveis = LISTAS_MYSUITE.filter(l =>
    !l.soQuandoTem || (listasDaTela[l.chave] || []).length);

  // Qual cartão está aberto decide o rótulo, a etiqueta e os itens da lista.
  const listaAberta = LISTAS_MYSUITE.find(l => l.chave === listaAtiva)
    || { chave: 'historico', label: 'Histórico', pill: null, vazio: '' };
  // As filas do MySuite saem na ordem de prioridade (Alta, Média, Baixa) e,
  // dentro dela, da previsão mais próxima — ver `ordenarPorPrioridade`. O
  // histórico é local e segue a ordem dele.
  const itensDaLista = listaAtiva === 'historico'
    ? historicoLocal
    : ordenarPorPrioridade(listasDaTela[listaAtiva] || []);

  // Quantas formalizações saíram hoje. A data no histórico é gravada com
  // toLocaleString('pt-BR'), então começa em "DD/MM/AAAA" — comparar o prefixo
  // basta e evita reinterpretar o formato.
  const formalizadasHoje = (() => {
    const hoje = new Date().toLocaleDateString('pt-BR');
    return historicoLocal.filter(e => String(e.data || '').startsWith(hoje)).length;
  })();

  // ═════════════════════════════════════════════════════════════════════════
  // FASE 1 — de quem é o ticket
  // ═════════════════════════════════════════════════════════════════════════
  if (fase === 1) {
    return (
      <>
        {reusoDe ? (
          /* Reutilização em andamento: o texto já está guardado, falta o ticket
             novo. O aviso fica aqui, e não em toast, porque ele tem de continuar
             visível enquanto o operador procura o número. */
          <div className="panel panel-dest d-info">
            <div className="panel-head">
              <div className="panel-title">
                Reutilizando o atendimento do ticket {reusoDe}
                <span className="sub">o texto foi carregado; o ticket tem de ser outro</span>
              </div>
              {/* Desistir da reutilização devolve a tela a um atendimento em
                  branco — inclusive tipo e classificação, que também vieram do
                  atendimento antigo. */}
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setReusoDe('');
                  setAnotacoes('');
                  setTextoFormalizado('');
                  setTipo('');
                  setAjusteAssunto('');
                  setAjusteClassificacao('');
                }}
              >
                <Icon name="x" size={12} /> Descartar
              </button>
            </div>
            <div className="campo-dica">
              Informe abaixo o <b>número do novo ticket</b>. O que foi escrito no
              atendimento anterior continua na fase seguinte, pronto para ser ajustado.
            </div>
          </div>
        ) : (
          <FlowHint icon="send">
            Informe o <b>número do ticket</b> para começar. O solicitante é resolvido
            automaticamente pelo MySuite, e a ocorrência atual do ticket já vem preenchida.
          </FlowHint>
        )}

        {/* Os quatro cartões são clicáveis: cada um troca a lista mostrada
            embaixo. O que está aberto fica marcado. */}
        {/* O Histórico é fixo; os do MySuite variam. Com cinco ou seis na
            linha, o grid de quatro colunas acompanha. */}
        <div className={`metrics ${cartoesVisiveis.length >= 4 ? `metrics-${cartoesVisiveis.length + 1}` : ''}`.trim()}>
          {cartoesVisiveis.map(l => (
            <Metric
              key={l.chave}
              icon={l.icone}
              valor={(listasDaTela[l.chave] || []).length}
              label={l.label}
              sub={operador?.codigo ? l.sub : 'sem cadastro no Orion'}
              ativo={listaAtiva === l.chave}
              alerta={!!l.alerta}
              aoClicar={() => setListaAtiva(l.chave)}
            />
          ))}
          <Metric
            icon="history"
            valor={historicoLocal.length}
            label="Histórico"
            sub={formalizadasHoje ? `${formalizadasHoje} hoje · neste computador` : 'neste computador'}
            ativo={listaAtiva === 'historico'}
            aoClicar={() => setListaAtiva('historico')}
          />
        </div>

        <div className="panel">
          <PanelHead titulo="Novo atendimento" sub="ticket e solicitante">
            {/* Atalho para quem só quer o texto passado a limpo, sem ticket.
                Discreto de propósito: é a exceção, não o caminho da casa. */}
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setRapidaAberta(true)}
              title="Formalizar um texto avulso, sem registrar em ticket"
            >
              <Icon name="zap" size={12} /> Formalização rápida
            </button>
          </PanelHead>

          <div className="detail-grid">
            <div>
              <label className="field-label">Nº do ticket</label>
              <div className="la-linha">
                <input
                  type="text"
                  value={numeroTicket}
                  onChange={e => setNumeroTicket(e.target.value)}
                  onBlur={resolverSolicitante}
                  onKeyDown={async e => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    await resolverSolicitante();
                    buscar();
                  }}
                  placeholder="Ex.: 123456"
                  autoFocus
                />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => buscar()}
                  disabled={buscandoTicket || !numeroTicket.trim()}
                >
                  {buscandoTicket
                    ? <Icon name="rotate-cw" size={13} className="orion-spin" />
                    : <Icon name="search" size={13} />}
                  Buscar
                </button>
              </div>
            </div>

            <div>
              <label className="field-label">Solicitante</label>
              <input
                type="text"
                value={solicitante}
                onChange={e => anotarSolicitante(e.target.value, numeroTicket)}
                placeholder="Resolvido pelo ticket"
              />
            </div>
          </div>
        </div>

        {/* Antes de "Meus atendimentos" de propósito: o que tem hora marcada
            decide o que fazer AGORA. Some sozinho quando não há retorno
            agendado — ver CardFilaRetorno. */}
        <CardFilaRetorno
          tickets={filaRetorno?.tickets}
          // Os que já tiveram o alerta e passaram da hora. O modal não volta
          // para eles — este cartão é onde continuam à vista.
          excedidos={filaRetorno?.excedidos}
          agora={filaRetorno?.agora}
          carregando={filaRetorno?.carregando}
          // Também passa pelo resumo: era o último lugar que abria a
          // formalização direto, sem o ticket estar em atendimento. Quem marca
          // o retorno como tratado agora é o resumo, quando o operador de fato
          // atende ou formaliza.
          aoAbrirTicket={abrirResumo}
        />

        {/* A lista do cartão aberto. Muda de conteúdo, não de lugar: o
            operador continua olhando para o mesmo ponto da tela. */}
        <div className="subhead first">
          {listaAberta.label}
          <span className="cnt">{itensDaLista.length}</span>
          {listaAtiva === 'historico' ? (
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => setView('historico')}
            >
              <Icon name="history" size={12} /> Abrir histórico completo
            </button>
          ) : (
            <button
              className="btn btn-ghost btn-sm"
              style={{ marginLeft: 'auto' }}
              // A fila de retorno tem consulta própria: atualizar a linha de
              // cartões tem de atualizar as duas, senão um número fica velho.
              onClick={atualizarFilas}
              disabled={atualizando}
              title="Consultar o MySuite de novo"
            >
              <Icon name="refresh-cw" size={12} className={filasCarregando ? 'orion-spin' : ''} /> Atualizar
            </button>
          )}
        </div>

        {listaAtiva === 'historico' ? (
          historicoLocal.length ? (
            <div className="sol-list">
              {historicoLocal.slice(0, 20).map(e => (
                <div
                  className="sol-card"
                  key={e.id || `${e.numero_ticket}-${e.data}`}
                  onClick={() => reutilizarFormalizacao(e)}
                >
                  <div className="sol-head">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="sol-meta">
                        {e.tipo_atendimento && <span className="pill st-concluida-pill">{e.tipo_atendimento}</span>}
                        {e.classificacao && <span className="sol-sistema">{e.classificacao}</span>}
                      </div>
                      <div className="sol-id">
                        {e.numero_ticket ? `#${e.numero_ticket}` : 'Sem ticket'}
                        {e.solicitante ? ` · ${e.solicitante}` : ''}
                        {e.data ? ` · ${e.data}` : ''}
                      </div>
                      <div className="sol-desc">{e.assunto || e.anotacoes || 'Sem assunto'}</div>
                    </div>
                    <div className="sol-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        title="Aproveita o texto deste atendimento em OUTRO ticket"
                        onClick={ev => { ev.stopPropagation(); reutilizarFormalizacao(e); }}
                      >
                        Reutilizar em outro ticket <Icon name="chevron-right" size={12} />
                      </button>
                      {e.numero_ticket && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={ev => { ev.stopPropagation(); abrirResumo(e.numero_ticket); }}
                        >
                          <Icon name="eye" size={12} /> Ver ticket
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState>Nenhuma formalização guardada neste computador ainda.</EmptyState>
          )
        ) : itensDaLista.length ? (
          <div className="sol-list">
            {itensDaLista.map(t => (
              <CardTicket
                key={numeroDoTicket(t) ?? t.codigo}
                ticket={t}
                pill={listaAberta.pill || pillDoStatus(t)}
                // Na fila de retorno o que importa é o prazo, não a classificação.
                extra={listaAtiva === 'filaRetorno'
                  ? textoDoPrazo(t, filaRetorno?.agora)
                  : null}
                aoAbrir={abrirResumo}
              />
            ))}
          </div>
        ) : (
          <EmptyState>
            {operador?.codigo
              ? listaAberta.vazio
              : 'Seu e-mail não está cadastrado em orion.operador — sem isso não dá para listar seus tickets.'}
          </EmptyState>
        )}

        {resumoTicket}

        {rapidaAberta && <FormalizacaoRapida aoFechar={() => setRapidaAberta(false)} />}
      </>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // Cabeçalho comum às fases 2 e 3
  // ═════════════════════════════════════════════════════════════════════════
  const cabecalho = (
    <div className="view-toolbar">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setFase(fase - 1)}>
          <Icon name="chevron-left" size={13} /> Voltar
        </button>
        <TrilhaFases fase={fase} />
      </div>
      <div className="ch-actions">
        <span className="ticket-tag"><Icon name="ticket" size={11} /> {numeroTicket}</span>
        {/* Abre o resumo do ticket que está sendo formalizado: é lá que a
            conversa inteira mora agora. */}
        <button className="btn btn-ghost btn-sm" onClick={() => abrirResumo(numeroTicket)}>
          <Icon name="eye" size={13} /> Ver ticket
        </button>
      </div>
    </div>
  );

  // ═════════════════════════════════════════════════════════════════════════
  // FASE 2 — o que aconteceu
  // ═════════════════════════════════════════════════════════════════════════
  if (fase === 2) {
    return (
      <>
        {cabecalho}

        {ticketInfo && (
          <div className="panel">
            <PanelHead titulo={ticketInfo.titulo || 'Ticket'} sub={solicitante} />
            <div className="sol-body">
              <div>
                <div className="fld-label">Ocorrência</div>
                <div className="fld-value">{ticketInfo.ocorrencia || 'Em branco'}</div>
              </div>
              <div>
                <div className="fld-label">Criticidade</div>
                <div className="fld-value">{ticketInfo.criticidade || 'Em branco'}</div>
              </div>
              <div>
                <div className="fld-label">Classificação</div>
                <div className="fld-value">{ticketInfo.classificacao || 'Em branco'}</div>
              </div>
              <div>
                <div className="fld-label">Previsão</div>
                <div className="fld-value">
                  {formatarPrevisao(ticketInfo.dataPrevisao, ticketInfo.horaPrevisao)}
                </div>
              </div>
            </div>
            <ObservacaoCliente cnpj={ticketInfo.cnpjCliente || ticketInfo.cnpj} />
          </div>
        )}

        <div className="panel">
          <PanelHead titulo="Tipo de atendimento" sub="define para onde o ticket vai" />
          <select value={tipo} onChange={e => setTipo(e.target.value)}>
            <option value="">— selecione —</option>
            {TIPOS_FORMALIZACAO.map(t => (
              <option key={t.valor} value={t.valor}>{t.rotulo}</option>
            ))}
          </select>

          {/* Agendado: a data e a hora combinadas com o cliente. Começam com a
              previsão que o ticket já tem (carregada na busca) e vão no envio. */}
          {tipo === 'Agendado' && (
            <div className="detail-grid" style={{ marginTop: 12 }}>
              <div>
                <label className="field-label">Data da previsão</label>
                <input type="date" value={previsaoData} onChange={e => setPrevisaoData(e.target.value)} />
              </div>
              <div>
                <label className="field-label">Hora da previsão</label>
                <input type="time" value={previsaoHora} onChange={e => setPrevisaoHora(e.target.value)} />
              </div>
            </div>
          )}

          {tipo === 'Suporte avançado' && (
            <label className="chk-linha" style={{ marginTop: 12 }}>
              <input
                type="checkbox"
                checked={possivelFalha}
                onChange={e => setPossivelFalha(e.target.checked)}
              />
              <span>
                Possível falha de sistema
                <span className="chk-dica">
                  Marcado, o ticket vai para a fila de Análise Suporte e a matriz GUT é exigida no envio.
                </span>
              </span>
            </label>
          )}
        </div>

        {/* "Contato sem sucesso" não passa pela IA: a interação é montada dos
            campos abaixo e vai direto para a revisão. */}
        {contatoSemSucesso ? (
          <div className="panel">
            <PanelHead titulo="Tentativas de contato" sub={`${tentativas.length} registrada(s)`}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setTentativas(l => [...l, { ...ESTADO_INICIAL_TENTATIVA }])}
              >
                <Icon name="plus" size={12} /> Mais uma
              </button>
            </PanelHead>

            {tentativas.map((t, i) => (
              <div className="detail-grid" key={i} style={{ marginBottom: 12 }}>
                <div>
                  <label className="field-label">Telefone</label>
                  <input
                    type="text"
                    value={t.telefone}
                    onChange={e => setTentativas(l => l.map((x, idx) => idx === i ? { ...x, telefone: e.target.value } : x))}
                  />
                </div>
                <div>
                  <label className="field-label">O que ocorreu</label>
                  <div className="la-linha">
                    <input
                      type="text"
                      value={t.motivo}
                      onChange={e => setTentativas(l => l.map((x, idx) => idx === i ? { ...x, motivo: e.target.value } : x))}
                      placeholder="Ex.: caixa postal"
                    />
                    {tentativas.length > 1 && (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setTentativas(l => l.filter((_, idx) => idx !== i))}
                        title="Remover tentativa"
                      >
                        <Icon name="x" size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Um campo só: o texto, as imagens NO ponto em que entram e o
                áudio no ícone do canto. A ordem das imagens aqui é a ordem em
                que elas saem no documento. */}
            <div className="panel">
              <PanelHead titulo="Anotações do atendimento" sub="o que vai virar o ticket" />
              <CampoAnotacoes
                valor={anotacoes}
                aoMudar={setAnotacoes}
                imagens={imagens}
                aoMudarImagens={setImagens}
                audio={audio}
                aoMudarAudio={setAudio}
                aoAvisar={m => avisar(m, { tipo: 'erro' })}
              />
              <div className="campo-dica" style={{ marginTop: 8 }}>
                Cole (Ctrl+V) ou arraste uma imagem para dentro do texto — ela entra
                onde o cursor estiver, e sai nessa ordem no documento. Com um áudio
                anexado, as anotações passam a ser opcionais: o áudio vira a fonte.
              </div>
            </div>

            {semelhantes.length > 0 && (
              // Âmbar quando é só parecido; vermelho quando algum passa de 85%
              // de proximidade — aí é provável que seja o MESMO incidente, e o
              // aviso precisa pesar mais, como o indicador de duplicata do 1.x.
              <div className={`panel panel-dest ${semelhantes.some(i => i.similarity >= 0.85) ? 'd-risco' : 'd-atual'}`}>
                <PanelHead titulo="Já existe incidente parecido" sub={`${semelhantes.length}`} />
                <div className="campo-dica" style={{ marginBottom: 12 }}>
                  Encontrado a partir do que você escreveu. Se for o mesmo erro, vale
                  associar este ticket ao incidente em vez de abrir outro.
                </div>
                <div className="sol-list" style={{ marginBottom: 0 }}>
                  {semelhantes.map(i => (
                    <div className="sol-card" key={i.id}>
                      <div className="sol-head">
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="sol-meta">
                            <StatusIncidentePill status={i.status} />
                            {i.numero_ticket && <span className="sol-id">#{i.numero_ticket}</span>}
                            {i.similarity != null && (
                              <span className={`inc-similaridade ${i.similarity >= 0.85 ? 'alta' : ''}`}>
                                {(i.similarity * 100).toFixed(0)}%
                              </span>
                            )}
                          </div>
                          <div className="sol-desc">{i.erro}</div>
                          {i.solucao_paliativa && (
                            <div className="sol-id">Contorno: {i.solucao_paliativa}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </>
        )}

        {/* A classificação do MySuite não aparece mais aqui: escrever o
            atendimento e classificar são dois momentos diferentes. A ocorrência
            e a criticidade continuam vindo do ticket na busca e podem ser
            trocadas na revisão, pela recomendação da IA ou pelo ajuste manual. */}

        <div className="acoes-fim">
          {/* O texto vai como foi escrito. Só com corpo suficiente — formalizar
              continua livre, porque a IA completa o que falta. */}
          {!contatoSemSucesso && (
            <button
              className="btn btn-ghost"
              onClick={enviarSemFormalizar}
              disabled={formalizando || !podeSemFormalizar}
              title={podeSemFormalizar
                ? 'Leva o texto para a revisão do jeito que está, sem a IA'
                : `Escreva ao menos ${MINIMO_SEM_FORMALIZAR} caracteres (${tamanhoSemFormalizar}/${MINIMO_SEM_FORMALIZAR})`}
            >
              <Icon name="send" size={14} /> Enviar sem formalizar
              {!podeSemFormalizar && (
                <span className="sem-formalizar-cnt">{tamanhoSemFormalizar}/{MINIMO_SEM_FORMALIZAR}</span>
              )}
            </button>
          )}
          <button className="btn btn-primary" onClick={formalizar} disabled={formalizando}>
            {formalizando
              ? <><Icon name="rotate-cw" size={14} className="orion-spin" />
                  {audio ? 'Transcrevendo e formalizando…' : 'Formalizando…'}</>
              : <><Icon name="zap" size={14} /> {contatoSemSucesso ? 'Registrar tentativas' : 'Formalizar ticket'}</>}
          </button>
        </div>

        {resumoTicket}
      </>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // FASE 3 — o que vai ser enviado
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <>
      {cabecalho}

      <div className="hd-revisao">
        <div className="hd-coluna">
          {!contatoSemSucesso && (
            // Verde: é proposta da IA, não o que está gravado. Era assim no 1.x
            // (a caixa "Sugestão da IA") e continua sendo o primeiro sinal que
            // o operador procura na tela.
            <div className="panel panel-dest d-ia">
              <PanelHead
                titulo="Sugestão da IA"
                sub={classificandoIa ? 'classificando…' : (classificacoesIa ? 'pronta' : 'não chegou')}
              >
                {classificandoIa && <Icon name="rotate-cw" size={14} className="orion-spin" />}
              </PanelHead>

              {classificacoesIa && !classificandoIa && (
                <>
                  <div className="sol-body">
                    <div>
                      <div className="fld-label">Assunto</div>
                      <div className="fld-value">{assuntoIa || '—'}</div>
                    </div>
                    <div>
                      <div className="fld-label">Classificação</div>
                      <div className="fld-value">{classificacoesIa.classificacao || '—'}</div>
                    </div>
                    <div>
                      <div className="fld-label">Ocorrência</div>
                      <div className="fld-value">{classificacoesIa.ocorrencia || '—'}</div>
                    </div>
                    <div>
                      <div className="fld-label">Criticidade</div>
                      <div className="fld-value">{classificacoesIa.criticidade || '—'}</div>
                    </div>
                  </div>

                  <div className="sol-actions" style={{ marginTop: 14 }}>
                    <button className="btn btn-primary btn-sm" onClick={aplicarIa} disabled={iaAplicada}>
                      <Icon name="check" size={12} /> {iaAplicada ? 'Aplicada' : 'Aplicar recomendação'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setAjusteAberto(v => !v)}>
                      <Icon name="pen" size={12} /> Ajustar manualmente
                    </button>
                  </div>
                </>
              )}

              {!classificandoIa && !classificacoesIa && (
                <div className="campo-dica">A classificação não chegou. Ajuste manualmente abaixo.</div>
              )}
            </div>
          )}

          {(ajusteAberto || contatoSemSucesso) && (
            // Ciano da marca: é a decisão do operador, e ela vence a da IA.
            <div className="panel panel-dest d-info">
              <PanelHead titulo="Ajuste manual" sub="tem prioridade sobre a IA" />
              <label className="field-label">Assunto</label>
              <input
                type="text"
                value={ajusteAssunto}
                onChange={e => setAjusteAssunto(e.target.value)}
                placeholder={ticketInfo?.titulo || 'Assunto do ticket'}
              />
              <label className="field-label" style={{ marginTop: 14 }}>Classificação</label>
              <input
                type="text"
                list="orion-classificacoes"
                value={ajusteClassificacao}
                onChange={e => setAjusteClassificacao(e.target.value)}
                placeholder={ticketInfo?.classificacao || 'Classificação'}
              />
              <datalist id="orion-classificacoes">
                {ordenarPorNome(listas.classificacoes).map(c => (
                  <option key={c.codigo} value={c.nome} />
                ))}
              </datalist>
            </div>
          )}

          {tipo === 'Suporte avançado' && possivelFalha && (
            <MatrizGut
              gut={gut}
              aoMudar={setGut}
              contexto={{
                numero_ticket: numeroTicket,
                solicitante,
                texto_final: textoLimpo,
                anotacoes: semMarcadores(anotacoes),
              }}
            />
          )}

          {mostrarPrevisao && (
            <div className="panel">
              <PanelHead
                titulo="Previsão"
                sub={formatarPrevisao(previsaoData || '0000-00-00', previsaoHora ? `${previsaoHora}:00` : '00:00:00')}
              />
              <div className="detail-grid">
                <div>
                  <label className="field-label">Data</label>
                  <input type="date" value={previsaoData} onChange={e => setPrevisaoData(e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Hora</label>
                  <input type="time" value={previsaoHora} onChange={e => setPrevisaoHora(e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="hd-coluna">
          {/* Editor, e não textarea: é aqui que o operador confere se cada
              imagem ficou no parágrafo certo depois da reescrita da IA — e
              move, se não ficou. O que ele vê é o que o documento desenha. */}
          <div className="panel">
            <PanelHead
              titulo={semFormalizacao ? 'Ticket (sem formalização)' : 'Ticket formalizado'}
              sub={idsNoTexto(textoFormalizado).length
                ? `revise o texto e a posição das ${idsNoTexto(textoFormalizado).length} imagem(ns)`
                : 'revise antes de enviar'}
            >
              {/* Privada: a interação fica só para a equipe no MySuite. */}
              <label
                className={`chk-privada${interacaoPrivada ? ' ativo' : ''}`}
                title="A interação fica visível só para a equipe — o cliente não vê"
              >
                <input
                  type="checkbox"
                  checked={interacaoPrivada}
                  onChange={e => setInteracaoPrivada(e.target.checked)}
                />
                <Icon name="lock" size={12} /> Mensagem privada
              </label>
            </PanelHead>
            <CampoAnotacoes
              alto
              comFormatacao
              valor={textoFormalizado}
              aoMudar={setTextoFormalizado}
              imagens={imagens}
              aoMudarImagens={setImagens}
              aoAvisar={m => avisar(m, { tipo: 'erro' })}
              placeholder="Texto que vai para o MySuite"
            />
            <div className="campo-dica" style={{ marginTop: 8 }}>
              As imagens voltaram na ordem em que você escreveu. O MySuite recebe só
              o texto; o documento sai com as imagens onde elas estão aqui.
            </div>
          </div>

          {sql && (
            <div className="panel">
              <PanelHead titulo="Código SQL" sub="extraído da formalização" />
              <textarea className="hd-texto" value={sql} readOnly rows={8} />
            </div>
          )}
        </div>
      </div>

      <div className="acoes-fim">
        <button className="btn btn-ghost" onClick={reiniciar}>
          <Icon name="x" size={14} /> Descartar
        </button>
        {/* O cliente trouxe outra situação no meio do atendimento: abre um
            ticket novo herdando solicitante, CNPJ e setor deste. */}
        <button className="btn btn-ghost" onClick={() => setNovoTicketAberto(true)}>
          <Icon name="plus" size={14} /> Criar ticket novo
        </button>
        <button className="btn btn-ghost" onClick={() => setMassaAberta(true)}>
          <Icon name="users" size={14} /> Criar em massa
        </button>
        <button className="btn btn-primary" onClick={revisar}>
          <Icon name="send" size={14} /> Enviar ticket
        </button>
      </div>

      {/* Última porta antes de o ticket mudar de fila: o operador confere em
          texto o que os códigos vão significar no MySuite. */}
      {validacao && (
        <div className="drawer-overlay" onClick={() => !enviando && setValidacao(null)}>
          <div className="modal-nx" onClick={e => e.stopPropagation()}>
            <div className="drawer-head">
              <div className="drawer-title">Confirmar envio</div>
              {/* De onde saiu a classificação que vai ser gravada. É só leitura
                  do que já está na tela — não muda nada no que é enviado —, mas
                  evita confirmar achando que foi a IA quando foi o ajuste, e
                  vice-versa. Verde = IA, ciano = operador, âmbar = MySuite. */}
              <span className={`selo-dest ${origemClassificacao.classe}`} style={{ marginLeft: 'auto' }}>
                {origemClassificacao.rotulo}
              </span>
              <button className="drawer-close" onClick={() => setValidacao(null)} disabled={enviando}>
                <Icon name="x" size={15} />
              </button>
            </div>

            <div className="drawer-body">
              <div className="sol-body">
                <div>
                  <div className="fld-label">Assunto</div>
                  <div className="fld-value">{validacao.resumo.assunto}</div>
                </div>
                <div>
                  <div className="fld-label">Classificação</div>
                  <div className="fld-value">{validacao.resumo.classificacao}</div>
                </div>
                <div>
                  <div className="fld-label">Ocorrência</div>
                  <div className="fld-value">{validacao.resumo.ocorrencia}</div>
                </div>
                <div>
                  <div className="fld-label">Criticidade</div>
                  <div className="fld-value">{validacao.resumo.criticidade}</div>
                </div>
                <div>
                  <div className="fld-label">Tipo de atendimento</div>
                  <div className="fld-value">{validacao.resumo.tipoAtendimento}</div>
                </div>
                {validacao.resumo.mostrarPrevisao && (
                  <div>
                    <div className="fld-label">Previsão</div>
                    <div className="fld-value">{validacao.resumo.previsao}</div>
                  </div>
                )}
              </div>

              {/* Para quem vai o Suporte avançado. Com ou sem "possível falha",
                  o ticket não cai na fila genérica: vai para um analista. */}
              {precisaDestino && (
                <div className="panel panel-dest d-info" style={{ marginTop: 16, marginBottom: 0 }}>
                  <PanelHead
                    titulo="Encaminhar para"
                    sub={possivelFalha
                      ? 'Suporte avançado com possível falha de sistema'
                      : 'Suporte avançado sem falha de sistema'}
                  />
                  <div className="detail-grid">
                    {DESTINOS_SUPORTE_AVANCADO.map(d => (
                      <label className="chk-linha" key={d.codigo}>
                        <input
                          type="radio"
                          name="destinoSupAvancado"
                          value={d.codigo}
                          checked={String(destinoSupAvancado) === String(d.codigo)}
                          onChange={() => setDestinoSupAvancado(String(d.codigo))}
                        />
                        <span>{d.nome}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {interacaoPrivada && (
                <div className="panel panel-dest d-risco" style={{ marginTop: 16, marginBottom: 0 }}>
                  <Icon name="lock" size={14} style={{ marginRight: 6 }} />
                  A interação vai como <b>privada</b>: só a equipe vê no MySuite, o cliente não.
                </div>
              )}

              {validacao.resumo.exigeGut && possivelFalha && (
                // Âmbar: o ticket vai mudar de fila, e isso é o que mais
                // costuma passar batido na confirmação.
                <div className="panel panel-dest d-atual" style={{ marginTop: 16, marginBottom: 0 }}>
                  <Icon name="alert-triangle" size={14} style={{ marginRight: 6 }} />
                  Suporte avançado com <b>possível falha de sistema</b>: o ticket vai para o
                  analista escolhido acima, com <b>GUT {gut.g * gut.u * gut.t}</b>
                  {' '}(G{gut.g} × U{gut.u} × T{gut.t}).
                </div>
              )}
            </div>

            <div className="drawer-foot">
              <button className="btn btn-ghost" onClick={() => setValidacao(null)} disabled={enviando}>
                Voltar
              </button>
              <button className="btn btn-primary" onClick={confirmarEnvio} disabled={enviando}>
                {enviando
                  ? <><Icon name="rotate-cw" size={14} className="orion-spin" /> Enviando…</>
                  : <><Icon name="send" size={14} /> Confirmar envio</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {posEnvio && (
        <PosEnvio
          tipo={posEnvio.tipo}
          numeroTicket={posEnvio.numeroTicket}
          // O documento é montado no cliente e sobe pronto. Por isso ele recebe
          // o texto COM os marcadores e o mapa das imagens: é o que permite
          // desenhar cada uma no ponto em que foi escrita, e não todas no fim.
          contextoDocumento={{
            texto: textoFormalizado,
            imagensPorId: Object.fromEntries(
              imagens.filter(i => i?.id).map(i => [i.id, i]),
            ),
            numeroTicket: posEnvio.numeroTicket,
            solicitante,
            assunto: posEnvio.resumo?.assunto || ajusteAssunto || ticketInfo?.titulo || '',
            tipoAtendimento: tipo,
            classificacao: posEnvio.resumo?.classificacao || '',
            ocorrencia: posEnvio.resumo?.ocorrencia || '',
            criticidade: posEnvio.resumo?.criticidade || '',
            operadorNome: operador?.nome || '',
            operadorEmail: operador?.email || '',
            dataHora: new Date().toLocaleString('pt-BR', {
              day: '2-digit', month: '2-digit', year: 'numeric',
              hour: '2-digit', minute: '2-digit',
            }),
          }}
          aoRegistrarIncidente={() => {
            // Não pula direto para o formulário: primeiro a IA lê o
            // atendimento e os prints são convertidos. O modal de transição
            // mostra isso acontecendo — leva alguns segundos.
            setPosEnvio(null);
            setTransicao({
              numeroTicket: posEnvio.numeroTicket,
              // Suporte avançado com possível falha nasce marcado como tal: é
              // o que coloca o incidente na fila do Setor de Sistemas.
              statusInicial: (tipo === 'Suporte avançado' && possivelFalha)
                ? 'pendente_possivel_sistemas'
                : 'pendente',
              gut: (tipo === 'Suporte avançado' && possivelFalha) ? gut : null,
            });
          }}
          aoFechar={() => { setPosEnvio(null); reiniciar(); }}
        />
      )}

      {novoTicketAberto && (
        <NovoTicket
          ticketOrigem={numeroTicket}
          ticketInfo={ticketInfo}
          solicitante={solicitante}
          listas={listas}
          aoFechar={() => setNovoTicketAberto(false)}
          aoCriado={(numero) => {
            // Ir para o ticket novo é recomeçar o fluxo por ele: a tela é
            // limpa e o número entra na fase 1, pronto para buscar.
            setNovoTicketAberto(false);
            reiniciar();
            setNumeroTicket(String(numero));
            avisar(`Ticket ${numero} carregado. Busque para continuar.`, { tipo: 'info' });
          }}
        />
      )}

      {transicao && (
        <TransicaoIncidente
          numeroTicket={transicao.numeroTicket}
          solicitante={solicitante}
          anotacoes={semMarcadores(anotacoes)}
          textoFinal={textoLimpo}
          imagens={imagens}
          statusInicial={transicao.statusInicial}
          gut={transicao.gut}
          aoCancelar={() => { setTransicao(null); reiniciar(); }}
          aoPronto={(pacote) => {
            setTransicao(null);
            reiniciar();
            abrirRegistroIncidente(pacote);
          }}
        />
      )}

      {massaAberta && (
        <TicketsEmMassa
          listas={listas}
          contexto={{
            titulo: ajusteAssunto || ticketInfo?.titulo || '',
            interacao: textoLimpo,
            tipo,
            codigoOcorrencia: ocorrencia,
            codigoCriticidade: criticidade,
            classificacao: ajusteClassificacao || ticketInfo?.classificacao || '',
            prioridade: ticketInfo?.prioridade,
            ticketInfo,
          }}
          aoFechar={() => setMassaAberta(false)}
        />
      )}

      {resumoTicket}
    </>
  );
}
